/**
 * Keeps the floating chrome layer (.ldg-fab-layer in index.css - the Back
 * pill, the Assistant/jump-to-top bubble stack, the freshness pill) clear of
 * the on-screen keyboard and the rare frame iOS Safari's address-bar
 * animation lags behind, on top of the `bottom: 0` anchor that rule already
 * gives everything for free. See index.css's own comment on this rule for
 * the fuller four-report history; the short version:
 *
 *   1. #378 - plain `position: fixed; bottom: Npx` on each control directly
 *      measured against the animated LAYOUT viewport and floated mid-scroll.
 *   2. #387 - the shared layer that replaced it was over-constrained
 *      (inset:0 plus an explicit height), so the browser discarded `bottom`
 *      and recomputed it from a frozen 100svh. Fixed with 100dvh.
 *   3. 2026-08-15, the Classes page - 100dvh alone still lags a frame on
 *      some WebKit builds and never reacts to the on-screen keyboard at
 *      all, so a focused search field could still shrink the visible area
 *      out from under it.
 *   4. 2026-08-18, Bobbie's iPhone - this hook used to write
 *      visualViewport's offsetTop/height straight onto the layer's own
 *      top/height, trusting both completely. A pinch-zoomed page, Safari's
 *      per-site Page Zoom, or iOS's accessibility Zoom (all consistent with
 *      the oversized type in her screenshots) reports a nonzero offsetTop,
 *      and nothing here validated it - one bad reading put the layer's top
 *      edge anywhere on the screen, stranding every control until the next
 *      scroll/resize happened to correct it. That is why it spanned hours
 *      and several pages instead of one moment.
 *
 * A later report (2026-10-03, Bobbie's iPhone, the Messages inbox) had the
 * WHOLE fixed frame, the top strip as well as this layer, sitting about one
 * keyboard height too high. Nothing in this file can do that (MAX_LIFT_PX
 * caps it at 80px and the top strip is not touched here), so it is not
 * answered here: see viewportResync.ts, which changes no number in this
 * file.
 *
 * index.css no longer lets top/height come from here at all - the layer
 * rests at `bottom: 0` unconditionally, which is already the right corner
 * with this hook not running. All this hook contributes now is a small
 * upward NUDGE on top of that resting position, `--hub-fab-vv-lift`,
 * clamped to [0, MAX_LIFT_PX] below before it is ever written. A wrong or
 * hostile visualViewport reading can therefore cost at most MAX_LIFT_PX of
 * drift from the true bottom corner - never a trip to mid-screen.
 */
import { useEffect } from 'react';

/**
 * How far this hook will ever nudge the FAB layer up from its `bottom: 0`
 * resting position, no matter what visualViewport reports. Sized to clear a
 * keyboard peeking a little over the layer, not a full keyboard, on
 * purpose: this hook has no way to tell "the keyboard is genuinely 300px
 * tall" apart from "offsetTop is garbage" from the numbers alone (both look
 * like a large gap between window.innerHeight and the visible viewport), so
 * it stays conservative either way. A control briefly under an open
 * keyboard is a small, self-correcting problem; a control stranded
 * mid-screen on every page for hours is the bug this file exists to close.
 *
 * 80, not a rounder number, because the Assistant bubble stacks 74px above
 * the jump-to-top pill it shares a corner with - the highest control in
 * this layer - and 74 + 80 = 154px keeps even that one inside the bottom
 * 20% of a 390x844 screen (the harness's own proof: see the "clamp
 * ceiling" case in tests/harness/fab-clearance/check.mjs) with margin to
 * spare. This is a fixed pixel budget, not a percentage of the screen, so
 * that fraction shrinks on a taller phone and grows on a shorter one; a
 * genuinely tiny device is out of scope here the same way it is for the
 * rest of this fix (see that harness file's honesty note).
 */
export const MAX_LIFT_PX = 80;

// A pinch-zoomed page, or one running under Safari's per-site Page Zoom,
// reports visualViewport.scale away from 1 - exactly the state that
// produces the nonzero/oversized offsetTop this hook cannot otherwise tell
// apart from a genuine keyboard gap (see report 4 above). Skip the
// enhancement entirely rather than guess at a scaled page; the bottom-0
// resting position underneath it is already correct.
export const SCALE_TOLERANCE = 0.02;

/**
 * The one plausibility gate for a visualViewport reading, shared with
 * viewportResync.ts (the 2026-10-03 keyboard re-sync) so both read the
 * device's numbers through the same check. Guard every input the lift
 * formula touches, not just height. A NaN/undefined/negative reading
 * anywhere here, or a scale that says the page is zoomed, is the
 * "implausible" case the whole redesign above exists for.
 */
export function isPlausibleViewportReading(
  layoutHeight: number,
  height: number,
  offsetTop: number,
  scale: number | undefined
): boolean {
  return (
    Number.isFinite(height) &&
    height > 0 &&
    Number.isFinite(offsetTop) &&
    offsetTop >= 0 &&
    Number.isFinite(layoutHeight) &&
    layoutHeight > 0 &&
    (typeof scale !== 'number' || Math.abs(scale - 1) <= SCALE_TOLERANCE)
  );
}

export function useFabViewportTracking(): void {
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const root = document.documentElement;
    const update = () => {
      const layoutHeight = window.innerHeight;
      const { height, offsetTop, scale } = vv;
      // An implausible reading (see isPlausibleViewportReading above) skips
      // the write and leaves whatever was already set (a prior good
      // measurement, or nothing) rather than computing a lift from numbers
      // that cannot be trusted. This is belt-and-braces, not the only
      // safety net: anything that DOES get written below is already
      // clamped to MAX_LIFT_PX, so skipping here cannot itself strand the
      // layer - it just avoids acting on data that makes no sense.
      if (!isPlausibleViewportReading(layoutHeight, height, offsetTop, scale)) return;
      // The gap between the layout viewport's true bottom and the bottom
      // of what is actually visible right now - covered by an on-screen
      // keyboard, or the odd frame the toolbar animation has not caught up
      // in. Clamped on both ends: never negative (that would push the
      // layer DOWN past its resting bottom-0 position), never past
      // MAX_LIFT_PX (that is the fail-safe ceiling, see the constant's own
      // comment) no matter how large a bad reading makes it look.
      const gap = layoutHeight - (offsetTop + height);
      const lift = Math.min(Math.max(gap, 0), MAX_LIFT_PX);
      root.style.setProperty('--hub-fab-vv-lift', `${lift}px`);
      root.setAttribute('data-vvh-ready', '1');
    };
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
      root.removeAttribute('data-vvh-ready');
      root.style.removeProperty('--hub-fab-vv-lift');
    };
  }, []);
}
